import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { SearchTenantMonitoringCompaniesQuery } from '@/lib/api/tenants/monitoring-company';
import { useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableFilterBar,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTableUtilityBar,
} from '@/components/data-table';
import {
  FilterForm,
  FilterField,
  FilterHeader,
  FilterInlineResetButton,
} from '@/components/filters/FilterForm';
import { ScrollArea } from '@/components/ui/scroll-area';
import { createTextColumn, createLinkColumn } from '@/components/tables/column-helpers';
import { TenantMonitoringCompanySearchDto } from '@/lib/api/types.gen';
import { Comparator, FilterOperator } from '@/lib/api/types.gen';

interface TeamTableData {
  id: string;
  name: string;
  primaryEmail: string;
  isDefault: boolean;
  ownedBy: string;
  ownedById: string | null;
  tenantId: string;
}

function extractTeamTableData(teams: TenantMonitoringCompanySearchDto[], tenantId: string): TeamTableData[] {
  return teams.map((team) => ({
    id: team.id || '',
    name: team.name || 'N/A',
    primaryEmail: team.primaryEmail || 'N/A',
    isDefault: team.isDefault || false,
    ownedBy: team.ownedBy || team.ownedById || 'N/A',
    ownedById: team.ownedById || null,
    tenantId: tenantId,
  }));
}

const teamColumns: ColumnDef<TeamTableData>[] = [
  createLinkColumn({
    id: 'name',
    accessorKey: 'name',
    label: 'Name',
    enableHiding: false,
    sortable: true,
    getHref: (row) => `/tenant/${row.tenantId}/team/${row.id}`,
  }),
  createTextColumn({
    id: 'primaryEmail',
    accessorKey: 'primaryEmail',
    label: 'Primary Email',
    sortable: true,
    cell: (value) => (
      <span className="text-gray-300">{value}</span>
    ),
  }),
  createTextColumn({
    id: 'isDefault',
    accessorKey: 'isDefault',
    label: 'Is Default',
    sortable: true,
    cell: (value) => (
      <span className="text-white/70">{value ? 'Yes' : 'No'}</span>
    ),
  }),
  createTextColumn({
    id: 'ownedBy',
    accessorKey: 'ownedBy',
    label: 'Owned By',
    sortable: true,
    cell: (value) => (
      <span className="text-white/70">{value}</span>
    ),
  }),
];

export function TeamList() {
  const navigate = useNavigate();
  const { tenant } = useAuthStore();
  const tenantId = tenant?.tenantId || '';

  const [urlParams, setUrlParams] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(20),
      name: parseAsString,
      primaryEmail: parseAsString,
      filters: parseAsString,
    },
    { history: 'replace' }
  );

  const page = urlParams.page;
  const pageSize = urlParams.pageSize;
  const nameFilter = urlParams.name || '';
  const emailFilter = urlParams.primaryEmail || '';
  const filtersOpen = urlParams.filters === 'open';

  const teamsQuery = useQuery({
    ...SearchTenantMonitoringCompaniesQuery({
      tenantId,
      pageNumber: page,
      pageSize,
      filterOperator: FilterOperator.And,
      filterFilterGroups:
        [
          [nameFilter, 'name', Comparator.ILike],
          [emailFilter, 'primaryEmail', Comparator.ILike],
        ]
          .filter(([val]) => val)
          .map(([value, field, action]) => ({
            operator: FilterOperator.And,
            filters: [{ field: field as string, value: value as string, action: action as Comparator }],
          })) || [],
    }),
    enabled: !!tenantId,
  });

  const teams = teamsQuery.data?.rows ?? [];
  const totalCount = teamsQuery.data?.totalRows ?? 0;
  
  const tableData = useMemo(() => {
    return extractTeamTableData(teams, tenantId);
  }, [teams, tenantId]);

  const filterScrollHeight = 'calc(100vh - 128px)';
  const filterCount = [nameFilter, emailFilter].filter((v) => v).length;

  const handlePageChange = useCallback(
    (newPage: number) => {
      setUrlParams({ page: newPage });
    },
    [setUrlParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setUrlParams({ pageSize: newPageSize, page: 1 });
    },
    [setUrlParams]
  );

  return (
    <div className="relative">
      <DataTable
        columns={teamColumns}
        data={tableData}
        loading={teamsQuery.isLoading}
        error={teamsQuery.error as Error | null}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[5, 10, 20, 100]}
        stateKey="teams-table"
        emptyMessage="No monitoring teams found."
        sidebarOpen={filtersOpen}
        onSidebarOpenChange={(open) => setUrlParams({ filters: open ? 'open' : null })}
      >
        <FilterForm
          defaultValues={{
            name: nameFilter || undefined,
            primaryEmail: emailFilter || undefined,
          }}
          onFiltersChange={({ values }) => {
            setUrlParams({
              name: (values.name as string) || null,
              primaryEmail: (values.primaryEmail as string) || null,
              page: 1,
            });
          }}
        >
          <DataTableContainer>
            <DataTableFilterBar>
              <div className="flex h-full flex-col">
                <FilterHeader onClose={() => setUrlParams({ filters: null })} />
                <ScrollArea style={{ height: filterScrollHeight }} className="flex-1 px-4">
                  <div className="space-y-4 pb-4 pt-2">
                    <FilterInlineResetButton
                      onReset={() => {
                        setUrlParams({
                          name: null,
                          primaryEmail: null,
                          page: 1,
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-4 pb-4">
                    <FilterField
                      type="text"
                      name="name"
                      label="Team Name"
                      field="name"
                      comparator={Comparator.ILike}
                      placeholder="Search team name..."
                    />
                    <FilterField
                      type="text"
                      name="primaryEmail"
                      label="Primary Email"
                      field="primaryEmail"
                      comparator={Comparator.ILike}
                      placeholder="Search email..."
                    />
                  </div>
                </ScrollArea>
              </div>
            </DataTableFilterBar>
            <DataTableContent>
              <div data-onboarding="teams-utility-bar">
                <DataTableUtilityBar
                  showFilterToggle
                  showColumnVisibility
                  filterCount={filterCount}
                  search={{
                    value: nameFilter,
                    onChange: (value) => setUrlParams({ name: value || null, page: 1 }),
                    placeholder: 'Search team name...',
                  }}
                  actions={
                    <Button
                      size="sm"
                      onClick={() => navigate('/team/add')}
                      data-onboarding="teams-add-button"
                    >
                      Add Team
                    </Button>
                  }
                />
              </div>
              <div data-onboarding="teams-table">
                <DataTableWrapper>
                  <DataTableHeader />
                  <DataTableRows />
                </DataTableWrapper>
              </div>
              <DataTableFooter />
            </DataTableContent>
          </DataTableContainer>
        </FilterForm>
      </DataTable>
    </div>
  );
}

export default TeamList;
